import { Loader2, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatPackagingDocumentFileSize } from './packagingDocumentUploadPreview'

export type PackagingDocumentUploadPreviewPhase =
  | 'confirm'
  | 'uploading'
  | 'success'

export type PackagingDocumentUploadPreviewPanelLabels = {
  uploading: string
  assessingFile: string
  multipleDropped: string
  confirmUpload: string
  chooseDifferentFile: string
  done: string
  success: string
}

type PackagingDocumentUploadPreviewPanelProps = {
  file: File
  previewUrl: string | null
  extraFilesIgnored: number
  phase: PackagingDocumentUploadPreviewPhase
  labels: PackagingDocumentUploadPreviewPanelLabels
  isAssessing?: boolean
  ineligibilityMessage?: string | null
  canConfirmUpload?: boolean
  onConfirmUpload: () => void
  onChooseDifferentFile: () => void
  onDone: () => void
  confirmUploadVariant?: 'default' | 'outline'
}

export function PackagingDocumentUploadPreviewPanel({
  file,
  previewUrl,
  extraFilesIgnored,
  phase,
  labels,
  isAssessing = false,
  ineligibilityMessage = null,
  canConfirmUpload = true,
  onConfirmUpload,
  onChooseDifferentFile,
  onDone,
  confirmUploadVariant = 'default',
}: PackagingDocumentUploadPreviewPanelProps) {
  const multipleDroppedMessage = labels.multipleDropped
    .replace('{{fileName}}', file.name)
    .replace('{{count}}', String(extraFilesIgnored + 1))

  const showUploading = phase === 'uploading'
  const showSuccess = phase === 'success'

  return (
    <div className='min-w-0 space-y-4 overflow-hidden rounded-lg border bg-muted/30 p-4'>
      {extraFilesIgnored > 0 && phase === 'confirm' && (
        <p className='break-all text-sm text-muted-foreground'>
          {multipleDroppedMessage}
        </p>
      )}

      <div className='flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start'>
        <div className='flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background'>
          {previewUrl != null ? (
            <img
              src={previewUrl}
              alt=''
              className='size-full object-contain'
            />
          ) : (
            <FileText className='size-10 text-muted-foreground' aria-hidden />
          )}
        </div>
        <div className='min-w-0 flex-1 space-y-1'>
          <p className='break-all text-sm font-medium' title={file.name}>
            {file.name}
          </p>
          <p className='text-xs text-muted-foreground tabular-nums'>
            {formatPackagingDocumentFileSize(file.size)}
          </p>
          {isAssessing && phase === 'confirm' && (
            <p className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='size-4 shrink-0 animate-spin' aria-hidden />
              {labels.assessingFile}
            </p>
          )}
          {!isAssessing && ineligibilityMessage != null && phase === 'confirm' && (
            <p className='text-sm text-destructive'>{ineligibilityMessage}</p>
          )}
          {showUploading && (
            <p className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='size-4 shrink-0 animate-spin' aria-hidden />
              {labels.uploading}
            </p>
          )}
          {showSuccess && <p className='text-sm'>{labels.success}</p>}
        </div>
      </div>

      {phase === 'confirm' && (
        <div className='flex min-w-0 flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onChooseDifferentFile}
          >
            {labels.chooseDifferentFile}
          </Button>
          <Button
            type='button'
            variant={confirmUploadVariant === 'outline' ? 'outline' : 'default'}
            size='sm'
            disabled={!canConfirmUpload || isAssessing}
            onClick={onConfirmUpload}
          >
            {labels.confirmUpload}
          </Button>
        </div>
      )}

      {showSuccess && (
        <div className='flex justify-end'>
          <Button type='button' size='sm' onClick={onDone}>
            {labels.done}
          </Button>
        </div>
      )}
    </div>
  )
}
